// The business cashflow view (PRD CF4; Chris, 2026-10-23): what comes in and goes out by week or
// month, booked in full and the pipeline weighted, with the net and the running balance from the
// latest bank balance. Below it, the controller's inputs: balances, open invoices, partner terms
// and the hourly payday to count from. The API sends dated flows; buckets and filters are here.

import { useMemo, useState } from "react";
import { Body1, Button, Caption1, Card, Field, Input, Select, Subtitle2, Switch, Tab, TabList } from "@fluentui/react-components";
import {
  CASH_IN,
  CASH_KIND_LABEL,
  CASH_OUT,
  cashPeriods,
  PARTNER_TYPES,
  REVENUE_TYPE_LABEL,
  type CashFlow,
  type CashKind,
  type CashSettings,
  type CashZoom,
  type OpenInvoice,
  type CashBalance,
} from "../../../core";
import { errorText, send, useApi } from "../api";
import { DateInput } from "../DateInput";
import { day, money } from "../format";
import { ErrorBar, Kpi, Loading, Page, Warnings } from "../ui";
import { BalanceChart, BalanceLegend } from "./Charts";

interface CashResponse {
  start: string;
  end: string;
  opening: { asOf: string; amount: number } | null;
  flows: CashFlow[];
  settings: CashSettings & { etag: string | null };
  clients: string[];
  access: { edit: boolean };
}

const HORIZONS = [3, 6, 12] as const;

export function Cash() {
  const [months, setMonths] = useState<number>(6);
  const [zoom, setZoom] = useState<CashZoom>("week");
  const [pipeline, setPipeline] = useState(true);
  const [client, setClient] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const data = useApi<CashResponse>(`/cash?months=${months}`);
  const d = data.data;

  const periods = useMemo(() => (d ? cashPeriods(d.flows, d.opening?.amount ?? 0, d.start, d.end, zoom, { pipeline, clientName: client || undefined }) : []), [d, zoom, pipeline, client]);
  const low = periods.reduce<(typeof periods)[number] | undefined>((m, p) => (!m || p.balance < m.balance ? p : m), undefined);
  const last = periods.at(-1);
  const overdue = periods.reduce((t, p) => t + p.overdue, 0);
  const labelOf = (key: string) => (zoom === "month" ? key : `wk ${key.slice(5)}`);
  const points = periods.map((p) => ({
    key: p.key,
    label: labelOf(p.key),
    balance: p.balance,
    ...(pipeline ? { withPipeline: p.balanceWithPipeline } : {}),
    cashIn: p.bookedIn + (pipeline ? p.pipelineIn : 0),
    cashOut: p.bookedOut + (pipeline ? p.pipelineOut : 0),
  }));
  const chosen = periods.find((p) => p.key === selected);

  const warnings: string[] = [];
  if (d && !d.opening) warnings.push("No bank balance yet, so the running balance starts at $0 today. Enter the balance as of a date below.");
  if (overdue > 0.5) warnings.push(`${money(overdue)} expected before the forecast starts hasn't been marked paid: it's shown in the first ${zoom}.`);
  const firstNegative = periods.find((p) => p.balance < 0);
  if (firstNegative && low) {
    const when = (p: typeof low) => (zoom === "month" ? p.key : `the week of ${p.key}`);
    warnings.push(`The booked balance goes below zero in ${when(firstNegative)} (${money(firstNegative.balance)}) and is lowest in ${when(low)} (${money(low.balance)}).`);
  }

  return (
    <Page title="Cash">
      <ErrorBar message={data.error} />
      {!d ? (
        <Loading />
      ) : (
        <>
          <div className="toolbar">
            <TabList size="small" selectedValue={zoom} onTabSelect={(_, v) => (setZoom(v.value as CashZoom), setSelected(null))}>
              <Tab value="week">Weeks</Tab>
              <Tab value="month">Months</Tab>
            </TabList>
            <Select size="small" aria-label="How far ahead" value={String(months)} onChange={(_, v) => (setMonths(Number(v.value)), setSelected(null))}>
              {HORIZONS.map((n) => (
                <option key={n} value={n}>
                  Next {n} months
                </option>
              ))}
            </Select>
            <Select size="small" aria-label="Client" value={client} onChange={(_, v) => setClient(v.value)}>
              <option value="">Every client</option>
              {d.clients.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <Switch label="Include the pipeline, weighted" checked={pipeline} onChange={(_, v) => setPipeline(v.checked)} />
          </div>
          <Warnings items={warnings} />
          <div className="kpis" aria-label="Cash forecast">
            <Kpi label="Opening balance" value={d.opening ? money(d.opening.amount) : "—"} sub={d.opening ? `As of ${day(d.opening.asOf)}` : "Not entered yet"} />
            <Kpi label="Lowest, booked" value={low ? money(low.balance) : "—"} tone={low && low.balance < 0 ? "bad" : undefined} flag="Below zero" sub={low ? `${zoom === "month" ? low.key : `Week of ${low.key}`}` : undefined} />
            <Kpi label={`Balance on ${day(d.end)}`} value={last ? money(last.balance) : "—"} sub="Booked work and company costs" />
            {pipeline && <Kpi label="With the pipeline" value={last ? money(last.balanceWithPipeline) : "—"} sub="Weighted by each project's probability" />}
          </div>
          <Card className="panel wide">
            <div className="panel-head">
              <Subtitle2>Running balance</Subtitle2>
              <BalanceLegend pipeline={pipeline} />
            </div>
            <Caption1>
              From {day(d.start)} to {day(d.end)}. Invoices land on the date payment is expected: the invoice date plus the client's terms, or the partner's for partner funding and
              referrals. Salaried pay goes out on the last day of each month and hourly pay every other Friday. {client ? "Company costs stay in when a client is picked: they're paid regardless." : ""} Click a {zoom} for its detail.
            </Caption1>
            <BalanceChart points={points} selected={selected} onSelect={setSelected} />
            <FlowTable periods={periods} pipeline={pipeline} zoom={zoom} labelOf={labelOf} selected={selected} onSelect={setSelected} />
          </Card>
          {chosen && <PeriodDetail flows={d.flows} from={chosen.from} to={chosen.to} pipeline={pipeline} client={client} title={zoom === "month" ? chosen.key : `Week of ${chosen.key}`} onClose={() => setSelected(null)} />}
          <CashInputs key={d.settings.etag ?? "new"} settings={d.settings} edit={d.access.edit} clients={d.clients} reload={data.reload} />
        </>
      )}
    </Page>
  );
}

function FlowTable(props: { periods: ReturnType<typeof cashPeriods>; pipeline: boolean; zoom: CashZoom; labelOf: (k: string) => string; selected: string | null; onSelect: (k: string | null) => void }) {
  const { periods } = props;
  const used = (kinds: readonly CashKind[], from: "booked" | "pipeline") => kinds.filter((k) => periods.some((p) => Math.abs(p[from][k] ?? 0) > 0.5));
  const row = (label: string, values: number[], cls?: string) => (
    <tr key={label} className={cls}>
      <th scope="row">{label}</th>
      {values.map((v, i) => (
        <td key={periods[i].key} className={`num${periods[i].key === props.selected ? " selected" : ""}`}>
          {Math.abs(v) < 0.5 ? "—" : money(v)}
        </td>
      ))}
    </tr>
  );
  return (
    <div className="table-scroll">
      <table className="edit-table cash-table">
        <thead>
          <tr>
            <th />
            {periods.map((p) => (
              <th key={p.key} className={`num${p.key === props.selected ? " selected" : ""}`}>
                <button type="button" className="link-button" onClick={() => props.onSelect(p.key === props.selected ? null : p.key)}>
                  {props.labelOf(p.key)}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr key="in" className="group">
            <th colSpan={periods.length + 1}>Coming in</th>
          </tr>
          {used(CASH_IN, "booked").map((k) => row(CASH_KIND_LABEL[k], periods.map((p) => p.booked[k] ?? 0)))}
          {props.pipeline && used(CASH_IN, "pipeline").length > 0 && row("Pipeline invoices, weighted", periods.map((p) => p.pipelineIn), "muted")}
          <tr key="out" className="group">
            <th colSpan={periods.length + 1}>Going out</th>
          </tr>
          {used(CASH_OUT, "booked").map((k) => row(CASH_KIND_LABEL[k], periods.map((p) => p.booked[k] ?? 0)))}
          {props.pipeline && used(CASH_OUT, "pipeline").length > 0 && row("Pipeline costs, weighted", periods.map((p) => p.pipelineOut), "muted")}
          {row("Net, booked", periods.map((p) => p.net), "total")}
          {row("Balance, booked", periods.map((p) => p.balance), "total")}
          {props.pipeline && row("Balance with the pipeline", periods.map((p) => p.balanceWithPipeline), "muted")}
        </tbody>
      </table>
    </div>
  );
}

function PeriodDetail(props: { flows: CashFlow[]; from: string; to: string; pipeline: boolean; client: string; title: string; onClose: () => void }) {
  // The same rule as the buckets: a client filter keeps that client's flows and the company's costs.
  const forClient = (f: CashFlow) => !props.client || f.clientName === props.client || (f.clientName === undefined && f.direction === "out");
  const rows = props.flows.filter((f) => f.date >= props.from && f.date <= props.to && (props.pipeline || !f.pipeline) && forClient(f));
  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>{props.title}</Subtitle2>
        <Button size="small" appearance="subtle" onClick={props.onClose}>
          Close
        </Button>
      </div>
      {rows.length === 0 ? (
        <Body1 className="muted">Nothing comes in or goes out.</Body1>
      ) : (
        <div className="table-scroll">
          <table className="edit-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>What</th>
                <th>Client</th>
                <th className="num">In</th>
                <th className="num">Out</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f, i) => (
                <tr key={i} className={f.pipeline ? "muted" : undefined}>
                  <td>{day(f.date)}</td>
                  <td>
                    {CASH_KIND_LABEL[f.kind]}: {f.label}
                    {f.pipeline && ` (pipeline, ${Math.round(f.weight * 100)}% of ${money(f.amount)})`}
                    {f.overdue && " · overdue"}
                  </td>
                  <td>{f.clientName ?? ""}</td>
                  <td className="num">{f.direction === "in" ? money(f.amount * f.weight) : ""}</td>
                  <td className="num">{f.direction === "out" ? money(f.amount * f.weight) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// --- The controller's inputs ---------------------------------------------------------------------------

function CashInputs(props: { settings: CashSettings & { etag: string | null }; edit: boolean; clients: string[]; reload: () => void }) {
  const s = props.settings;
  const [balances, setBalances] = useState<CashBalance[]>(s.balances);
  const [invoices, setInvoices] = useState<OpenInvoice[]>(s.openInvoices);
  const [terms, setTerms] = useState(s.partnerTerms);
  const [anchor, setAnchor] = useState(s.hourlyPayAnchor);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const dirty = JSON.stringify([balances, invoices, terms, anchor]) !== JSON.stringify([s.balances, s.openInvoices, s.partnerTerms, s.hourlyPayAnchor]);
  const edit = props.edit;
  const setB = (i: number, patch: Partial<CashBalance>) => setBalances(balances.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const setV = (i: number, patch: Partial<OpenInvoice>) => setInvoices(invoices.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const num = (v: string) => (v === "" ? (undefined as unknown as number) : Number(v));
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/cash/settings", { etag: s.etag, balances, openInvoices: invoices, partnerTerms: terms, hourlyPayAnchor: anchor });
      props.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const sorted = balances.map((b, i) => ({ b, i })).sort((a, z) => z.b.asOf.localeCompare(a.b.asOf));
  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>What the forecast starts from</Subtitle2>
        {edit && (
          <Button appearance="primary" size="small" disabled={!dirty || busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        )}
      </div>
      <Caption1>Until QuickBooks connects, the controller enters the bank balance each month and the invoices already sent. {edit ? "" : "Only someone who closes periods can change these."}</Caption1>
      <ErrorBar message={error} />

      <div className="panel-head">
        <Body1>
          <strong>Bank balances</strong>: the forecast starts the day after the latest.
        </Body1>
        {edit && (
          <Button size="small" onClick={() => setBalances([...balances, { id: crypto.randomUUID(), asOf: "", amount: 0, by: "", at: "" }])}>
            Add a balance
          </Button>
        )}
      </div>
      {balances.length === 0 ? (
        <Body1 className="muted">None yet.</Body1>
      ) : (
        <table className="edit-table">
          <thead>
            <tr>
              <th>As of</th>
              <th className="num">Balance</th>
              <th>Note</th>
              <th>Entered</th>
              {edit && <th aria-label="Remove" />}
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ b, i }) => (
              <tr key={b.id}>
                <td>
                  <DateInput size="small" value={b.asOf} disabled={!edit} onChange={(_, d) => setB(i, { asOf: d.value })} aria-label="As of" />
                </td>
                <td>
                  <Input size="small" type="number" className="money-input" contentBefore="$" value={String(b.amount)} disabled={!edit} onChange={(_, d) => setB(i, { amount: Number(d.value) })} aria-label="Balance" />
                </td>
                <td>
                  <Input size="small" value={b.note ?? ""} disabled={!edit} placeholder="e.g. operating + savings" onChange={(_, d) => setB(i, { note: d.value || undefined })} aria-label="Note" />
                </td>
                <td className="muted">{b.by ? `${b.by}, ${day(b.at.slice(0, 10))}` : "Not saved yet"}</td>
                {edit && (
                  <td>
                    <Button size="small" appearance="subtle" onClick={() => setBalances(balances.filter((_, j) => j !== i))}>
                      Remove
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="panel-head">
        <Body1>
          <strong>Open invoices</strong>: sent and not yet paid. Each is expected on its date plus the client's terms; mark it paid when the money arrives.
        </Body1>
        {edit && (
          <Button size="small" onClick={() => setInvoices([...invoices, { id: crypto.randomUUID(), clientName: props.clients[0] ?? "", number: "", amount: 0, invoiceDate: "" }])}>
            Add an invoice
          </Button>
        )}
      </div>
      {invoices.length === 0 ? (
        <Body1 className="muted">None entered.</Body1>
      ) : (
        <div className="table-scroll">
          <table className="edit-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Invoice</th>
                <th className="num">Amount</th>
                <th>Invoice date</th>
                <th className="num">Terms</th>
                <th>Paid on</th>
                {edit && <th aria-label="Remove" />}
              </tr>
            </thead>
            <tbody>
              {invoices.map((v, i) => (
                <tr key={v.id} className={v.paidOn ? "muted" : undefined}>
                  <td>
                    <Input size="small" list="cash-clients" value={v.clientName} disabled={!edit} onChange={(_, d) => setV(i, { clientName: d.value })} aria-label="Client" />
                  </td>
                  <td>
                    <Input size="small" value={v.number} disabled={!edit} placeholder="e.g. 1042" onChange={(_, d) => setV(i, { number: d.value })} aria-label="Invoice number" />
                  </td>
                  <td>
                    <Input size="small" type="number" className="money-input" contentBefore="$" value={String(v.amount)} disabled={!edit} onChange={(_, d) => setV(i, { amount: Number(d.value) })} aria-label="Amount" />
                  </td>
                  <td>
                    <DateInput size="small" value={v.invoiceDate} disabled={!edit} onChange={(_, d) => setV(i, { invoiceDate: d.value })} aria-label="Invoice date" />
                  </td>
                  <td>
                    <Input
                      size="small"
                      type="number"
                      className="cover-input"
                      contentBefore="Net"
                      placeholder="client's"
                      value={v.termsDays === undefined ? "" : String(v.termsDays)}
                      disabled={!edit}
                      onChange={(_, d) => setV(i, { termsDays: num(d.value) })}
                      aria-label="Terms for this invoice"
                    />
                  </td>
                  <td>
                    <DateInput size="small" value={v.paidOn ?? ""} disabled={!edit} onChange={(_, d) => setV(i, { paidOn: d.value || undefined })} aria-label="Paid on" />
                  </td>
                  {edit && (
                    <td>
                      <Button size="small" appearance="subtle" onClick={() => setInvoices(invoices.filter((_, j) => j !== i))}>
                        Remove
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <datalist id="cash-clients">
        {props.clients.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      <div className="form-grid three">
        {PARTNER_TYPES.map((t) => (
          <Field key={t} label={`${REVENUE_TYPE_LABEL[t]} terms`} hint="Paid by the partner, not the client: days from invoice to payment.">
            <Input type="number" min={0} max={180} contentBefore="Net" value={String(terms[t] ?? 45)} disabled={!edit} onChange={(_, d) => setTerms({ ...terms, [t]: num(d.value) })} />
          </Field>
        ))}
        <Field label="An hourly payday" hint="Any Friday hourly staff were paid; paydays fall every other Friday from it.">
          <DateInput value={anchor} disabled={!edit} onChange={(_, d) => setAnchor(d.value)} />
        </Field>
      </div>
    </Card>
  );
}
